// src/lib/authMode.js
//
// Rollout gate for the login flow. The server decides (AUTH_LOGIN_MODE) and the
// client asks once at startup, so switching between the legacy ADAL implicit
// login and the server-side MSAL login is a server env change only.
//
// In "msal" mode the browser never holds an Azure token or the Client Token:
// the server keeps both and sets an HttpOnly session cookie. What the UI does
// need - who is signed in, their client and groups (for telemetry, chat
// history keys, profile and feature gating) - comes from the server's session
// summary and is kept in memory, with only the non-secret user/client names and
// timestamps mirrored into localStorage for the existing idle-timeout code.
import axios from "axios";
import { parseToken } from "./helpers";

const CSRF_HEADER = "X-Requested-With";
const CSRF_HEADER_VALUE = "bighorn";
const MODE_TIMEOUT_MS = 5000;
// Stands in for the Client Token wherever UI code only asks "is there a
// session?". It is not a credential and is never stored; the cookie is what
// authenticates, and the server ignores this value.
const SESSION_MARKER = "cookie-session";

let mode = "adal";
let sessionInfo = null;

const serverUrl = () => process.env.REACT_APP_BIGHORN_SERVER;

export const getAuthMode = () => mode;
export const isMsalMode = () => mode === "msal";

// Resolves to the effective mode; any failure leaves the legacy flow in place.
export const loadAuthMode = async () => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), MODE_TIMEOUT_MS);
  try {
    const response = await fetch(`${serverUrl()}/api/auth/config`, {
      cache: "no-store",
      signal: controller ? controller.signal : undefined,
    });
    const body = await response.json();
    mode = body && body.mode === "msal" ? "msal" : "adal";
  } catch (err) {
    mode = "adal";
  } finally {
    if (timer) clearTimeout(timer);
  }
  return mode;
};

// Only requests to the Bighorn API get the cookie and CSRF header: sending
// credentials to other origins (Power BI, blob storage) would break their CORS
// and leak the session context.
const isServerUrl = (url) => {
  const base = (serverUrl() || "").replace(/\/+$/, "");
  if (typeof url !== "string" || !base) return false;
  // A bare prefix match would also accept "<base>.evil.test".
  return url === base || ["/", "?", "#"].some((separator) => url.startsWith(base + separator));
};

const setHeader = (headers, name, value) => {
  if (typeof headers.set === "function") headers.set(name, value);
  else headers[name] = value;
};

// Existing call sites build their own axios/fetch requests (and still pass a
// ClientToken header that is now empty), so credentials and the CSRF header are
// attached centrally rather than at each call site.
export const installCredentialedTransport = () => {
  axios.interceptors.request.use((config) => {
    const url = /^https?:\/\//i.test(config.url || "") ? config.url : (config.baseURL || "") + (config.url || "");
    if (isServerUrl(url)) {
      config.withCredentials = true;
      config.headers = config.headers || {};
      setHeader(config.headers, CSRF_HEADER, CSRF_HEADER_VALUE);
    }
    return config;
  });

  if (typeof window !== "undefined" && window.fetch && !window.fetch.bighornCredentialed) {
    const nativeFetch = window.fetch.bind(window);
    const wrapped = (input, init = {}) => {
      const url = typeof input === "string" ? input : input && input.url;
      if (!isServerUrl(url)) return nativeFetch(input, init);
      const headers = new Headers(init.headers || (typeof input !== "string" && input.headers) || undefined);
      headers.set(CSRF_HEADER, CSRF_HEADER_VALUE);
      return nativeFetch(input, { ...init, headers, credentials: "include" });
    };
    wrapped.bighornCredentialed = true;
    window.fetch = wrapped;
  }
};

export const fetchServerSession = async () => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), MODE_TIMEOUT_MS);
  try {
    const response = await fetch(`${serverUrl()}/api/auth/session`, {
      credentials: "include",
      cache: "no-store",
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) return { authenticated: false };
    return await response.json();
  } catch (err) {
    return { authenticated: false };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const SESSION_KEYS = ["expires_at", "session_start", "last_activity", "user", "client", "sso"];

// Mirrors the server's view of the session into memory (claims) and the
// non-secret localStorage keys the idle/absolute-timeout and telemetry code
// reads. The server stays the authority: a missing or expired cookie clears
// them, so a stale tab can't look signed in.
export const applyServerSession = (session) => {
  if (!session || !session.authenticated) {
    sessionInfo = null;
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    return false;
  }
  sessionInfo = {
    user: session.user,
    client: session.client,
    groups: Array.isArray(session.groups) ? session.groups : [],
    kind: session.kind,
  };
  localStorage.setItem("expires_at", session.exp * 1000);
  localStorage.setItem("session_start", session.iat * 1000);
  if (!localStorage.getItem("last_activity")) {
    localStorage.setItem("last_activity", Date.now());
  }
  localStorage.setItem("sso", session.kind === "sso" ? "true" : "false");
  if (session.user) localStorage.setItem("user", session.user);
  if (session.client) localStorage.setItem("client", String(session.client).split("+")[0]);
  return true;
};

// The applicationAuth response carries the same summary as `session` (it has no
// `authenticated` flag, being a response to an authenticated call).
export const applyAuthResponseSession = (session) => applyServerSession(session && { ...session, authenticated: true });

// What UI code reads where it used to look at the Client Token. Header call
// sites keep reading localStorage directly, which is simply empty in msal mode.
export const getClientToken = () => {
  if (isMsalMode()) return sessionInfo ? SESSION_MARKER : null;
  return localStorage.getItem("clientToken");
};

// The claims the UI gates on ({ client, groups, user }), from the server's
// session summary in msal mode or from decoding the legacy Client Token.
export const getSessionClaims = () => {
  if (isMsalMode()) {
    return sessionInfo ? { client: sessionInfo.client, groups: sessionInfo.groups, name: sessionInfo.user } : undefined;
  }
  return parseToken(localStorage.getItem("clientToken"));
};

export const startServerLogin = ({ returnTo, loginHint } = {}) => {
  const params = new URLSearchParams();
  if (returnTo) params.set("returnTo", returnTo);
  if (loginHint) params.set("login_hint", loginHint);
  const query = params.toString();
  window.location.assign(`${serverUrl()}/api/auth/login${query ? "?" + query : ""}`);
};

// Exchanges the one-time ovis "preview as client" token (from the URL fragment)
// for a cookie session. Resolves to the session summary or throws.
export const redeemPreviewToken = async (token) => {
  const response = await axios.post(`${serverUrl()}/api/auth/setClient`, { token });
  if (!response.data || !response.data.success) {
    throw new Error("Unable to open the client preview");
  }
  return response.data.session;
};

// Credentials the legacy (ADAL / header-token) login left in this browser:
// the Azure id_token, the Client Token, a stored preview token and the ADAL/MSAL
// token caches. Once the server is in msal mode nothing needs them, and they
// are exactly what the HttpOnly cookie is meant to replace - so a user who
// logged in before the switch must not keep them. The new cookie session and
// its non-secret timestamps are untouched.
const LEGACY_LOCAL_KEYS = ["access_token", "id_token", "clientToken", "setClientToken", "jwt-token"];
const TOKEN_CACHE_KEY = /^(adal|msal)\./i;

export const clearLegacyCredentials = () => {
  const removeCachedTokens = (storage) => {
    if (typeof storage === "undefined" || !storage) return;
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (typeof key === "string" && TOKEN_CACHE_KEY.test(key)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  };

  if (typeof localStorage !== "undefined") {
    LEGACY_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
  }
  removeCachedTokens(typeof localStorage !== "undefined" ? localStorage : undefined);
  removeCachedTokens(typeof sessionStorage !== "undefined" ? sessionStorage : undefined);
};

export const bootstrapAuth = async () => {
  await loadAuthMode();
  if (!isMsalMode()) return mode;
  clearLegacyCredentials();
  installCredentialedTransport();
  applyServerSession(await fetchServerSession());
  // The login form sets this flag just before handing off to the server, and
  // sessionStorage survives the round trip through Azure. Nothing on the way
  // back clears it (the ADAL callback used to), so it would leave the portal
  // stuck on "Authenticating..." and switch off the request interceptors.
  if (typeof sessionStorage !== "undefined") sessionStorage.removeItem("logging_in");
  return mode;
};
